import React, { useState } from 'react';
import {
  CoverSlide,
  ContentSlide,
  TwoColumnSlide,
  TableSlide,
} from '../../../commercial-proposal';
import '../../../commercial-proposal/styles/Presentation.css';

const Presentation: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const handleMouseLeave = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    setShowControls(false);
  };

  // Footer común para todas las slides
  const commonFooter = {
    estimationCode: 'Estimación / SM085-06',
    versionText: 'Versión 1.0 — Dic 31, 2025',
    confidentialText: 'Confidencial',
    websiteUrl: 'www.simphony.com.co',
  };

  const slides = [
    // Slide 1: Portada
    <CoverSlide
      key="cover"
      title="Deprisa - Propuesta Equipo de trabajo TI"
      footer={commonFooter}
    />,

    // Slide 2: Contexto actual
    <ContentSlide
      key="context"
      title="Contexto actual"
      content={
        <div>
          <p>
            Deprisa está en una etapa de evolución y escalamiento de su operación digital, con
            múltiples iniciativas corriendo en paralelo y una necesidad creciente de coordinación,
            visibilidad y consistencia en la ejecución.
          </p>
          <p>
            Para acelerar esta <strong>ejecución</strong>, proponemos incorporar un PMO (Project Manager
            Operations) y un Líder Técnico que conviertan planes en entregables: destrabar dependencias,
            alinear equipos y stakeholders, mantener cadencia de releases, asegurar calidad y seguimiento
            diario, y cerrar cada iniciativa con resultados medibles en producción.
          </p>
        </div>
      }
      footer={commonFooter}
    />,

    // Slide 3: Roles y responsabilidades
    <TwoColumnSlide
      key="roles"
      leftColumn={{
        title: 'Project Manager Operator',
        subtitle: 'Ejecución con ritmo, visibilidad y control.',
        backgroundColor: '#f5f5f5',
        items: [
          { number: '01', text: 'Priorización de demanda y mantenimiento del roadmap' },
          { number: '02', text: 'Cadencia operativa: metodología, procesos y seguimiento' },
          { number: '03', text: 'Gestión de riesgos, bloqueos y dependencias (RAID)' },
          { number: '04', text: 'Reporting ejecutivo y trazabilidad de decisiones' },
          { number: '05', text: 'Control de cambios y cierre de iniciativas con resultados' },
        ],
      }}
      rightColumn={{
        title: 'Líder Técnico',
        subtitle: 'Decisiones rápidas, calidad y entregas constantes.',
        backgroundColor: '#ffffff',
        items: [
          { number: '01', text: 'Liderar la ejecución técnica end-to-end por iniciativa' },
          { number: '02', text: 'Definir enfoque/arquitectura y estándares de ingeniería' },
          { number: '03', text: 'Destrabar al equipo: decisiones y alineación técnica diaria' },
          { number: '04', text: 'Asegurar calidad y releases (reviews, QA, checklist)' },
          { number: '05', text: 'Fortalecer capacidades: mentoring y buenas prácticas (SDLC/DevOps)' },
        ],
      }}
      footer={commonFooter}
    />,

    // Slide 4: Tabla de inversión
    <TableSlide
      key="pricing"
      title="Inversión para asegurar ejecución"
      subtitle="Capacidad dedicada por rol (160h/mes) + horas adicionales bajo demanda"
      headers={['Rol Staff', 'Tarifa / hora', 'Base mensual (160h)', 'Tarifa / hora (extra)']}
      rows={[
        {
          'Rol Staff': 'Project Manager Operator',
          'Tarifa / hora': '$ 60.000 COP',
          'Base mensual (160h)': '$ 9.600.000 COP',
          'Tarifa / hora (extra)': '$ 70.000 COP',
        },
        {
          'Rol Staff': 'Líder Técnico',
          'Tarifa / hora': '$ 70.000 COP',
          'Base mensual (160h)': '$ 11.200.000 COP',
          'Tarifa / hora (extra)': '$ 80.000 COP',
        },
      ]}
      footer={commonFooter}
    />,
  ];

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (e.key === 'Escape') {
      window.history.back();
    }
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown as any);
    return () => window.removeEventListener('keydown', handleKeyDown as any);
  });

  return (
    <div className="presentation-container">
      {slides[currentSlide]}
      <div
        className="top-controls-zone"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      <div
        className={`top-controls ${showControls ? 'visible' : ''}`}
        onMouseMove={handleMouseMove}
      >
        <button className="back-button" onClick={() => window.history.back()}>
          ← Volver al inicio
        </button>
        <div className="slide-navigation">
          <button
            onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
          >
            ← Anterior
          </button>
          <span>
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
            disabled={currentSlide === slides.length - 1}
          >
            Siguiente →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Presentation;
